//Third Party Dependencies
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Alert } from 'reactstrap';

//MHD General Purpose libraries
import config from '../../config';
import ActiveErrors from '../../components/common/activeErrors';
import { SectionHeading } from '../../components/common/standardComponents';
import LoadingIndicator from '../../components/loadingIndicator';

//Specific Business and UI components
import * as orderManagementActions from '../../actions/orderManagementActions';
import OrderDetailsModal from '../../components/modals/orderDetailsModal';
import OrderForm from '../../components/orderManagement/createOrderForm';
import OrderHistoryTable from '../../components/orderManagement/orderHistoryTable';
import PatientDetailsStrip from '../../components/patient/patientDetailsStrip';
import { useAppDispatch, useAppSelector } from '../../store';
import { add } from 'date-fns';
import async from 'react-select/dist/declarations/src/async/index';

//Constants
const CREATE_ORDER_SUCCESS_MESSAGE = 'SUCCESS! The followup order has been created for your patient.';
const EDIT_ORDER_SUCCESS_MESSAGE = 'SUCCESS! The order edit has been saved.';
const ADD_APPOINTMENT_MESSAGE = 'Please add at least one appointment to the followup order.';
const NO_CONSUMER_PRODUCT_INSTANCE_MESSAGE = 'No consumer product instances configured. Please contact support.';
const SAVED_DATE_RANGE = 'savedDateRange';
const CUSTOM_DATE_RANGE = 'customDateRange';

interface Appointment {
	externalReferralOrderId?: any;
	department?: any;
	provider?: any;
	requiresReferralToBook?: any;
	visitType?: any;
	visitTypeName?: any;
	referringDepartment?: any;
	referringProvider?: any;
	referringProviderName?: any;
	referringSiteId?: any;
	referringServiceId?: any;
	siteId?: any;
	serviceId?: any;
	unmanagedReferringServiceName?: any;
	subgroupId?: any;
}

interface OrderVisit {
	minVisitDate?: string;
	maxVisitDate?: string;
	systemDateRangeId?: any;
	appointments?: Appointment[]
}

const CreateOrderView = () => {
    const dispatch = useAppDispatch();

    const activePatient = useAppSelector((state) => state.activePatient);
    const auth = useAppSelector((state) => state.auth);
	const configState = useAppSelector((state) => state.config);
	const hasMultipleProductInstances = useAppSelector((state) => state.config.consumerProductInstances?.filter((pi:any) => pi.id > 0).length > 1);
	const pageData = useAppSelector((state) => state.orderManagement.careOrderDetailsPageData);
	const consumerProductInstances = useAppSelector((state) => state.config.consumerProductInstances);
	const token = useAppSelector((state) => state.auth.token);
	const orderManagement = useAppSelector((state) => state.orderManagement);

	const [selectionState, setSelectionState] = useState({
		selectedDateRangeOption: SAVED_DATE_RANGE,
		selectedDateRange: 0,
		selectedConsumerProductInstance: hasMultipleProductInstances || (consumerProductInstances.length < 1) ? 0 : consumerProductInstances[0].id
	})

	const [appointments, setAppointments] = useState<any[]>([
		{
			externalReferralOrderId: "",
			department: 0,
			provider: 0,
			requiresReferralToBook: false,
			visitType: 0,
			visitTypeName: "",
			referringDepartment: 0,
			referringProvider: 0,
			referringProviderName: "",
		}
	]);
	const [alertColor, setAlertColor] = useState<string>('warning');
	const [alertMessage, setAlertMessage] = useState<string>('');
	const [enableEditMode, setEnableEditMode] = useState<boolean>(false);
	const [isBookableSitesLoaded, setIsBookableSitesLoaded] = useState<boolean>(false);
	const [isCareOrderDateRangesLoaded, setIsCareOrderDateRangesLoaded] = useState<boolean>(false);
	const [isCareOrderDetailsPageDataLoaded, setIsCareOrderDetailsPageDataLoaded] = useState<boolean>(false);
	const [isOrderHistoryLoaded, setIsOrderHistoryLoaded] = useState<boolean>(false);
	const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
	const [isSavingCreate, setIsSavingCreate] = useState<boolean>(false);
	const [isSavingEdit, setIsSavingEdit] = useState<boolean>(false);
	const [orderDetailsModalVisible, setOrderDetailsModalVisible] = useState<boolean>(false);
	const [selectedOrder, setSelectedOrder] = useState<any>({});
	const [selectedOrderStatusDisplay, setSelectedOrderStatusDisplay] = useState<string>('');
	const [selectedOrderStatusNotes, setSelectedOrderStatusNotes] = useState<string>('');
	const [selectedStatusCode, setSelectedStatusCode] = useState<string>('');
	const [selectedStatusReasonId, setSelectedStatusReasonId] = useState<string>('');
	const [showAlert, setShowAlert] = useState<boolean>(false);
	const [showValidationErrors, setShowValidationErrors] = useState<boolean>(false);
	const [customDateRangeEnd, setCustomDateRangeEnd] = useState<any>(null);
	const [customDateRangeEndIsValid, setCustomDateRangeEndIsValid] = useState<boolean>(false);
	const [customDateRangeEndPicker, setCustomDateRangeEndPicker] = useState<Date | null>(null);
	const [customDateRangeStart, setCustomDateRangeStart] = useState<any>(null);
	const [customDateRangeStartIsValid, setCustomDateRangeStartIsValid] = useState<boolean>(false);
	const [customDateRangeStartPicker, setCustomDateRangeStartPicker] = useState<Date | null>(null);
	const [selectedSchedulableOrders, setSelectedSchedulableOrders] = useState<any[]>([]);
	const [isSchedulableOrdersModalOpen, setIsSchedulableOrdersModalOpen] = useState<boolean>(false);
	const [disableAddAdditionalOrders, setDisableAddAdditionalOrders] = useState<boolean>(false);
	const [isSubgroupsLoaded, setIsSubgroupsLoaded] = useState<boolean>(false);
	const [showStatusReasonList, setShwStatusReaosonList] = useState<any>(null);

	const validateEntireOrder = (order: any) => {
		const deepFlatten = (obj:any): any => {
			return Object.values(obj).reduce((agg:any, curr: any) => {
				if (typeof curr !== "object") {
					return agg.concat(curr);
				} else {
					return agg.concat(deepFlatten(curr));
				}
			}, []);
		};

		let cleanOrder = order;
		if (cleanOrder && cleanOrder.visits.length > 0) {
			cleanOrder.visits.forEach((o: OrderVisit) => {
				if (selectionState.selectedDateRangeOption === SAVED_DATE_RANGE) {
					delete o.minVisitDate;
					delete o.maxVisitDate;
				} else if (selectionState.selectedDateRangeOption === CUSTOM_DATE_RANGE) {
					delete o.systemDateRangeId;
				}
				o.appointments?.forEach((a: Appointment) => {
					if (!a.requiresReferralToBook) {
						delete a.referringSiteId;
						delete a.referringServiceId;
					}
					delete a.requiresReferralToBook;
					if (a.externalReferralOrderId !== "") {
						delete a.siteId;
						delete a.serviceId;
					} else {
						delete a.unmanagedReferringServiceName;
						delete a.externalReferralOrderId;
						delete a.subgroupId;
					}
				})
			})
		}

		let result = deepFlatten(cleanOrder).every((x:any) => x);
		return result;
	}

	const onCreateOrderClick = async() => {
		let consumerIdProductInstance = parseInt(selectionState.selectedConsumerProductInstance, 10);
		const order = {
			systemId: auth.referralSystemId,
			patientReferenceId: activePatient.details.referenceId,
			idProductInstance: auth.productInstanceId,
			consumerIdProductInstance: consumerIdProductInstance,
			visits: [
				{
					systemDateRangeId: selectionState.selectedDateRangeOption === SAVED_DATE_RANGE ? selectionState.selectedDateRange : null,
					minVisitDate: selectionState.selectedDateRangeOption === CUSTOM_DATE_RANGE ? customDateRangeStart : null,
					maxVisitDate: selectionState.selectedDateRangeOption === CUSTOM_DATE_RANGE ? customDateRangeEnd : null,
					appointments: appointments.map(x => {
						return {
							siteId: x.department === 0 ? "" : x.department,
							serviceId: x.provider === 0 ? "" : x.provider,
							requiresReferralToBook: x.requiresReferralToBook,
							appointmentTypeId: x.visitType === 0 ? "" : x.visitType,
							referringSiteId: x.referringDepartment === 0 ? "" : x.referringDepartment,
							referringServiceId: x.referringProvider === 0 ? "" : x.referringProvider,
							unmanagedReferringServiceName: x.referringProviderName,
							externalReferralOrderId: x.externalReferralOrderId,
							subgroupId: x.subgroupId ?? ''
						};
					}),
				},
			],
		};

		if (consumerProductInstances.filter((pi: any) => pi.id > 0).length === 0) {
			setShowAlert(true);
			setAlertColor('danger');
			setAlertMessage(NO_CONSUMER_PRODUCT_INSTANCE_MESSAGE);
		}
		else if (!order.visits[0].appointments.length) {
			setShowAlert(true);
			setAlertMessage(ADD_APPOINTMENT_MESSAGE);
		}
		else if (validateEntireOrder(order)) {
			let hasMultipleProdInstances = hasMultipleProductInstances;
			let firstProductInstanceId = consumerProductInstances[0].id;

			setIsSavingCreate(true);
			try {
				var response = await dispatch(orderManagementActions.createCareOrder(order));
				if (!response.error) {
					setAppointments([
						{
							externalReferralOrderId: "",
							department: "",
							provider: "",
							requiresReferralToBook: false,
							visitType: "",
							referringDepartment: "",
							referringProvider: "",
						}
					]);
					setAlertColor('success');
					setAlertMessage(CREATE_ORDER_SUCCESS_MESSAGE);
					setShowAlert(true);
					setShowValidationErrors(false);
					setCustomDateRangeEnd('');
					setCustomDateRangeEndIsValid(false);
					setCustomDateRangeEndPicker(null);
					setCustomDateRangeStart('');
					setCustomDateRangeStartIsValid(false);
					setCustomDateRangeStartPicker(null);
					setSelectionState((prev: any) => ({
						...prev,
						selectedDateRangeOption: SAVED_DATE_RANGE,
						selectedDateRange: 0,
						selectedConsumerProductInstance: hasMultipleProdInstances ? 0 : firstProductInstanceId
					}));
				}

			}
			catch {

			}
			finally {
				setIsSavingCreate(false);
				setIsOrderHistoryLoaded(false);
				let searchCriteria = {
					patientReferenceId: activePatient.details.referenceId,
				};
				dispatch(orderManagementActions.searchCareOrders(searchCriteria))
					.then(() => {
						setIsOrderHistoryLoaded(true);
					});
			}
		}
		else {
			setShowValidationErrors(true);
		}
	}

	const onAddAppointmentClick = () => {
		const updatedAppointments = [...appointments].concat({
			externalReferralOrderId: "",
			department: "",
			provider: "",
			visitType: "",
			referringDepartment: "",
			referringProvider: "",
		});
		setAppointments(updatedAppointments);
		setShowAlert(false);
		setAlertMessage('');
	}

	const onChange = (e: any, name: string) => {
		let localName = name && name !== "" ? name : e.target.name;
		setSelectionState((prev) => ({ ...prev, [localName]: e.target.value }));
	}

	const onCustomDateRangeChange = (event: any, error: any) => {
		const { name, value } = event.target;
		let pickerValue = (value === "__/__/____" || value === "" || !moment(value).isValid()) ? new Date() : new Date(value);
		let datePickerName = `${name}Picker`;
		let isValidName = `${name}IsValid`;

		if (name === 'customDateRangeStart') {
			setCustomDateRangeStart(value);
			setCustomDateRangeStartIsValid(!error);
			setCustomDateRangeStartPicker(pickerValue);
		}
		else if (name === 'customDateRangeEnd') {
			setCustomDateRangeEnd(value);
			setCustomDateRangeEndIsValid(!error);
			setCustomDateRangeEndPicker(pickerValue);

			const isEndDateValid = customDateRangeStartIsValid && moment(value).isAfter(moment(customDateRangeStart));
			setCustomDateRangeEndIsValid(isEndDateValid);
		}

	};

	const validateCustomDate = (value: any) => {
		let error = false;
		let mDate = moment(value).startOf('day');
		if (mDate.isBefore(moment().startOf('day'))) {
			error = true;
		}
		return error;
	}


	const onDatePickerChange = (date: any, name: string) => {
		let dateTextName = `${name}Picker`;
		let mDate = moment(date);

		if (dateTextName === 'customDateRangeStartPicker') {
			setCustomDateRangeStartPicker(date);
		}
		else if (dateTextName === 'customDateRangeEndPicker') {
			setCustomDateRangeEndPicker(date);
		}

		let event = {
			target: {
				name: name,
				value: mDate.format("MM/DD/YYYY"),
			}
		};
		let error = validateCustomDate(date);
		onCustomDateRangeChange(event, error);
	}

	const updateAppointmentOption = (index: number, dropdown: keyof Appointment, value: any) => {
		const updatedAppointments = [...appointments];
		const optionToChange = { ...updatedAppointments[index] };

		let intValue = parseInt(value, 10);
		optionToChange[dropdown] = intValue;

		switch (dropdown) {
			case "department":
				dispatch(orderManagementActions.retrieveServicesForSite(intValue, index));
				optionToChange.provider = 0;
				optionToChange.visitType = 0;
				break;
			case "provider":
				let selectedProvider = orderManagement.rowOptions[index].providers.find((p: any) => p.id === intValue);
				dispatch(orderManagementActions.retrieveAppointmentTypesForCalendar(appointments[index].department, intValue, index));
				optionToChange.visitType = 0;
				optionToChange.requiresReferralToBook = selectedProvider.requiresReferralToBook;
				break;
			case "referringDepartment":
				dispatch(orderManagementActions.retrieveReferringProvidersForSite(intValue, index));
				optionToChange.referringProvider = 0;
				break;
			default:
				break;
		}

		updatedAppointments[index] = optionToChange;
		setAppointments(updatedAppointments);
	}

	const onRowDropdownChange = (event: any, index: number) => {
		updateAppointmentOption(index, event.target.name, event.target.value);
	}

	const onRemoveRowClick = (index: number) => {
		dispatch(orderManagementActions.removeAppointmentOptionRow(index));

		let updatedAppointments = appointments;
		let apptToRemove = updatedAppointments.splice(index, 1);
		const selectedSchedulableOrdersFilter = selectedSchedulableOrders.filter((s: any) => s !== apptToRemove[0].externalReferralOrderId);

		let disableAddAdditionalOrders = updatedAppointments.length === configState.orderManagement.maxAppointmentsPerVisit;
		setAppointments(updatedAppointments);
		setSelectedSchedulableOrders(selectedSchedulableOrdersFilter);
		setDisableAddAdditionalOrders(disableAddAdditionalOrders);
	}

	const onDetailsClick = (e: any, id: number) => {
		e.preventDefault();
		dispatch(orderManagementActions.getCareOrderHistory(id));
		let order = orderManagement.careOrders.find((o: any) => o.id === id);
		resolveStatusDisplayAndVisibility(order);

		setIsSaveDisabled(true);
		setOrderDetailsModalVisible(true);
		setSelectedOrder(order);
		setSelectedOrderStatusNotes(order.notes);
	}

	const onSaveDetails = async () => {
		let orderUpdate = {
			id: selectedOrder.id,
			statusCode: selectedStatusCode,
			statusReason: selectedStatusReasonId,
			notes: selectedOrderStatusNotes
		};

		setIsSavingEdit(true);

		try {
			var response = await dispatch(orderManagementActions.updateCareOrder(orderUpdate))
			if (!response.error) {
				setShowAlert(true);
				setAlertColor('success');
				setAlertMessage(EDIT_ORDER_SUCCESS_MESSAGE);
				setEnableEditMode(false);
				setIsSaveDisabled(true);
				setOrderDetailsModalVisible(false);
				setSelectedOrderStatusDisplay('');
				setSelectedOrderStatusNotes('');
				setSelectedStatusCode('');
				setSelectedStatusReasonId('');
			}
			else {
				setOrderDetailsModalVisible(false);
			}

		}
		catch {

		}
		finally {
			let searchCriteria = {
				patientReferenceId: activePatient.details.referenceId,
			};
			setIsSavingEdit(false);
			setIsOrderHistoryLoaded(false);
			
			dispatch(orderManagementActions.searchCareOrders(searchCriteria))
				.then(() => {
					setIsOrderHistoryLoaded(true);
				});

		}
	}

	const onCancelDetails = () => {
		setEnableEditMode(false);
		setIsSaveDisabled(true);
		setOrderDetailsModalVisible(false);
	}

	const onEditClick = (e: any) => {
		e.preventDefault();
		let editMode = !enableEditMode;
		let { statusCode, statusReasonId } = selectedOrder;

		setEnableEditMode(editMode);
		setSelectedStatusCode(statusCode);
		setSelectedStatusReasonId(statusReasonId.toString());
	}

	const onStatusListChange = (e: any) => {
		e.preventDefault();
		let selectedStatusCode = e.target.value;
		const filteredStatusReasons = pageData.careOrderStatusReasons.filter((r: any) => r.code === selectedStatusCode);
		setSelectedStatusCode(selectedStatusCode);
		if (filteredStatusReasons.length > 0)
			setSelectedStatusReasonId(filteredStatusReasons[0].id.toString());
		else
			setSelectedStatusReasonId('');
		resolveSaveDisabled();
	}

	const onStatusReasonListChange = (e: any) => {
		let selectedStatusReasonId = e.target.value;
		setSelectedStatusReasonId(selectedStatusReasonId);
		resolveSaveDisabled();
	}

	const onStatusNotesChange = (e: any) => {
		setSelectedOrderStatusNotes(e.target.value);
		resolveSaveDisabled();
	}

	const onAddRadiologyOrdersClick = (e: any) => {
		setIsSchedulableOrdersModalOpen(true);
	}

	const onSchedulableOrdersSave = (e: any) => {
		let selectedOrders = activePatient.schedulableOrders.filter((o: any) => selectedSchedulableOrders.includes(o.idExternal));
		let newSelectedOrders = selectedOrders.filter((s: any) => appointments.every(a => a.externalReferralOrderId !== s.idExternal));

		let newAppointments = newSelectedOrders.map((o: any) => {
			return {
				department: 0,
				provider: 0,
				visitType: o.appointmentTypeId,
				visitTypeName: o.appointmentTypeName,
				referringDepartment: 0,
				referringProvider: 0,
				referringProviderName: o.referringProviderName,
				requiresReferralToBook: false,
				externalReferralOrderId: o.idExternal,
				subgroup: 0,
			};
		});

		let finalAppointments = appointments.filter(a => a.externalReferralOrderId === "" || (a.externalReferralOrderId !== "" && selectedOrders.some((s: any) => s.idExternal === a.externalReferralOrderId))).concat(newAppointments);
		setAppointments(finalAppointments);
		setIsSchedulableOrdersModalOpen(false);
	}

	const onSchedulableOrdersClose = (e: any) => {
		setIsSchedulableOrdersModalOpen(false);
	}

	const onSchedulableOrderChange = (e: any) => {
		let updatedSelectedSchedulableOrders = { ...selectedSchedulableOrders }
		if (updatedSelectedSchedulableOrders.length > 0 && updatedSelectedSchedulableOrders.includes(e.target.id)) {
			updatedSelectedSchedulableOrders.splice(updatedSelectedSchedulableOrders.indexOf(e.target.id), 1);
		}
		else {
			updatedSelectedSchedulableOrders.push(e.target.id);
		}

		let total = updatedSelectedSchedulableOrders.length + appointments.filter(a => !updatedSelectedSchedulableOrders.some(s => s === a.externalReferralOrderId)).length;
		if (total > 0) {
			let maxRows = configState.orderManagement.maxAppointmentsPerVisit;
			setDisableAddAdditionalOrders(total >= maxRows);
		}
		setSelectedSchedulableOrders(updatedSelectedSchedulableOrders);
	}

	const dismissAlert = () => {
		setShowAlert(false);
		setAlertMessage('');
	}

	const shouldValidate = () => {
		return showValidationErrors;
	}

	const resolveSaveDisabled = () => {
		let isDisabled = true;
		if (selectedStatusCode !== selectedOrder.statusCode) {
			isDisabled = false;
		} else if (selectedStatusReasonId !== selectedOrder.statusReasonId) {
			isDisabled = false;
		} else if (selectedOrderStatusNotes) {
			isDisabled = false;
		}
		setIsSaveDisabled(isDisabled);
	}

	const resolveStatusDisplayAndVisibility = (order: any) => {
		let orderStatusDisplay = '';

		switch (order.statusCode.toLowerCase()) {
			case config.careOrderStatusCodes.open.toLowerCase():
				orderStatusDisplay = order.status;
				break;
			case config.careOrderStatusCodes.closed.toLowerCase():
				orderStatusDisplay = `${order.status} (${order.statusReason})`;
				break;
			default:
				orderStatusDisplay = "-";
				break;
		}
		setSelectedOrderStatusDisplay(orderStatusDisplay);
	}

	const getCareOrderHistory = async() => {
		let searchCriteria = {
			patientReferenceId: activePatient.details.referenceId,
		};
		await dispatch(orderManagementActions.searchCareOrders(searchCriteria))
			.then(() => {
				setIsOrderHistoryLoaded(true);
			});
	
	}

	useEffect(() => {
		const fetchInitialData = async () => {
			try {
				await dispatch(orderManagementActions.retrieveCareOrderDateRanges(auth.referralSystemId));
				setIsCareOrderDateRangesLoaded(true);

				await dispatch(orderManagementActions.retrieveBookableSites(auth.referralSiteId));
				setIsBookableSitesLoaded(true);

				await dispatch(orderManagementActions.getCareOrderDetailsPageData(true));
				setIsCareOrderDetailsPageDataLoaded(true);

				if (configState.orderManagement.usesExternalOrders) {
					await dispatch(orderManagementActions.retrieveSubgroupsForSystem());
				}
				setIsSubgroupsLoaded(true);
				await getCareOrderHistory();
			}
			catch (error: any) {
				console.error("Error loading initial data: ", error);
			}
		};
		fetchInitialData();
	}, [])

	//TODO: Build filter control/logic
	let openOrders = orderManagement.careOrders.filter((o: any) => o.statusCode && o.statusCode.toLowerCase() === config.careOrderStatusCodes?.open?.toLowerCase());
	let isLoaded = isCareOrderDateRangesLoaded && isBookableSitesLoaded && isCareOrderDetailsPageDataLoaded && isOrderHistoryLoaded && isSubgroupsLoaded;
	let enableSave =selectionState.selectedDateRangeOption === SAVED_DATE_RANGE || (selectionState.selectedDateRangeOption === CUSTOM_DATE_RANGE && customDateRangeEndIsValid && customDateRangeStartIsValid);

	return (
		<>
			<ActiveErrors />
			<SectionHeading label="Create Followup Visit Order" />
			<Alert className="text-center" color={alertColor} isOpen={showAlert} toggle={dismissAlert}>
				{alertMessage}
			</Alert>

			{!isLoaded &&
				<LoadingIndicator />
			}
			{isLoaded &&
				<div>
					<PatientDetailsStrip patientDetails={activePatient.details} config={configState.patient} />
					<OrderForm
						appointments={appointments}
						customDateRangeEnd={customDateRangeEnd}
						customDateRangeEndIsValid={customDateRangeEndIsValid}
						customDateRangeEndPicker={customDateRangeEndPicker}
						customDateRangeStart={customDateRangeStart}
						customDateRangeStartIsValid={customDateRangeStartIsValid}
						customDateRangeStartPicker={customDateRangeStartPicker}
						dateRangeList={orderManagement.dateRangeList}
						departmentList={orderManagement.bookableSites}
						disableAdditionalOrders={disableAddAdditionalOrders}
						enableProductInstances={hasMultipleProductInstances}
						enableSave={enableSave}
						isSavingCreate={isSavingCreate}
						isSchedulableOrdersModalOpen={isSchedulableOrdersModalOpen}
						maxRows={configState.orderManagement.maxAppointmentsPerVisit}
						onAddAppointmentClick={onAddAppointmentClick}
						onAddRadiologyOrderClick={onAddRadiologyOrdersClick}
						onCreateOrderClick={onCreateOrderClick}
						onCustomDateRangeChange={onCustomDateRangeChange}
						onDatePickerChange={onDatePickerChange}
						onDateRangeOptionChange={onChange}
						onDropdownChange={onChange}
						onRemoveRowClick={onRemoveRowClick}
						onRowDropdownChange={onRowDropdownChange}
						onSchedulableOrderChange={onSchedulableOrderChange}
						onSchedulableOrdersClose={onSchedulableOrdersClose}
						onSchedulableOrdersSave={onSchedulableOrdersSave}
						optionLists={orderManagement.rowOptions}
						patientDetails={activePatient.details}
						productInstances={consumerProductInstances}
						schedulableOrdersList={activePatient.schedulableOrders}
						selectedConsumerProductInstance={selectionState.selectedConsumerProductInstance}
						selectedDateRange={selectionState.selectedDateRange}
						selectedDateRangeOption={selectionState.selectedDateRangeOption}
						selectedSchedulableOrdersList={selectedSchedulableOrders}
						shouldValidate={shouldValidate}
						subgroups={orderManagement.subgroups}
						validateCustomDateRange={validateCustomDate}

					/>
					<OrderHistoryTable orders={openOrders} onDetailsClick={onDetailsClick} isLoaded={isOrderHistoryLoaded} showPatientInfo={false} isParentCreateOrder={true} />
					<OrderDetailsModal
						careOrderHistory={orderManagement.careOrderHistory}
						isEditMode={enableEditMode}
						isOpen={orderDetailsModalVisible}
						isSaveDisabled={isSaveDisabled}
						isSavingEdit={isSavingEdit}
						onCancel={onCancelDetails}
						onConfirm={onSaveDetails}
						onEdit={onEditClick}
						onStatusListChange={onStatusListChange}
						onStatusNotesChange={onStatusNotesChange}
						onStatusReasonListChange={onStatusReasonListChange}
						order={selectedOrder}
						orderStatusDisplay={selectedOrderStatusDisplay}
						patientConfig={configState.patient}
						selectedOrderStatusNotes={selectedOrderStatusNotes}
						selectedStatusCode={selectedStatusCode}
						selectedStatusReasonId={selectedStatusReasonId}
						showStatusReasonList={showStatusReasonList}
						statusOptionsList={pageData.careOrderStatuses}
						statusReasonOptionsList={pageData.careOrderStatusReasons}
						isParentCreateOrder={true}
					/>
				</div>
			}
		</>
	);
}
export default CreateOrderView;